<template>
  <div class="instructor-login">
    <div class="login-container">
      <div class="login-header">
        <h1>👨‍🏫 講師ログイン</h1>
        <p>オトエイゴ講師専用管理画面</p>
      </div>

      <div class="login-form">
        <form @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="email">メールアドレス</label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              placeholder="講師のメールアドレス"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="password">パスワード</label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              placeholder="パスワード"
              class="form-input"
            />
          </div>

          <button type="submit" class="btn btn-primary" :disabled="isLoading">
            {{ isLoading ? 'ログイン中...' : 'ログイン' }}
          </button>
        </form>

        <div class="auth-links">
          <p>講師アカウントをお持ちでない場合</p>
          <router-link to="/instructor/signup" class="link">
            講師アカウント新規登録
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '../../supabaseClient'

const router = useRouter()
const email = ref('')
const password = ref('')
const isLoading = ref(false)

const handleLogin = async () => {
  if (isLoading.value) return
  
  isLoading.value = true
  
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.value,
      password: password.value
    })

    if (error) throw error

    if (data.user) {
      console.log('✅ 講師ログイン成功:', data.user)
      
      // 初回ログイン時にプロフィールを作成/更新（RPC経由でRLSを回避）
      try {
        const appRole = data.user.user_metadata?.app_role || null
        const fullName = data.user.user_metadata?.full_name || null
        
        if (appRole || fullName) {
          console.log('📝 プロフィールupsert実行:', { appRole, fullName })
          const { error: rpcError } = await supabase.rpc('rpc_upsert_profile', {
            app_role: appRole,
            full_name: fullName
          })
          
          if (rpcError) {
            console.error('⚠️ プロフィールupsert警告:', rpcError)
            // エラーでもログインは続行（プロフィールは後で手動修正可能）
          } else {
            console.log('✅ プロフィールupsert完了')
          }
        }
      } catch (rpcError) {
        console.error('⚠️ プロフィールupsert例外:', rpcError)
        // エラーでもログインは続行
      }
      
      // 講師ホームへ遷移
      router.push('/instructor/home')
    }
  } catch (error) {
    console.error('❌ 講師ログインエラー:', error)
    alert('ログインに失敗しました。メールアドレスとパスワードを確認してください。')
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.instructor-login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem;
}

.login-container {
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.login-header h1 {
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
  font-weight: 700;
}

.login-header p {
  color: #6b7280;
  margin: 0;
  font-size: 1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.btn {
  width: 100%;
  padding: 0.875rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
  transform: translateY(-1px);
}

.auth-links {
  margin-top: 1.5rem;
  text-align: center;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.auth-links p {
  margin: 0 0 0.5rem 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.link {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
}

.link:hover {
  text-decoration: underline;
}

/* スマホ対応 */
@media (max-width: 480px) {
  .login-container {
    padding: 2rem 1.5rem;
    margin: 1rem;
  }
  
  .login-header h1 {
    font-size: 1.6rem;
  }
}
</style>
